/**
 * AP-48 4.4, 7. — **Behinderungsliste** im Listen-Standard: je Kette eine Zeile (Art, Beschreibung, erster und
 * letzter Tag, Arbeitstage, dauert an, Mehrkosten, angezeigt am, Berichte) — die Grundlage einer
 * Mehrkostenforderung (TB-7). FilterBar, Spalten über `ui-settings`, Sortierung und Seiten auf dem Server,
 * Export Excel/PDF samt Mail. Muster: `SiteDocsListPage`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowLeft, ArrowUp, ArrowUpDown, Check, Settings } from 'lucide-react'
import { TAGEBUCH_BEHINDERUNG_ARTEN, TAGEBUCH_BEHINDERUNG_SPALTEN, type SiteDocProjectChoice, type TagebuchBehinderungArt, type TagebuchBehinderungsKette } from '@symphenia/shared-types'
import ModuleLockedPage from '@/components/ModuleLockedPage'
import { FilterBar } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { ExportButton } from '@/components/ui/ExportButton'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDate } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { useTerminology } from '@/lib/useTerminology'
import { behinderungenExportRequest, fetchBehinderungen, fetchProjectChoices, type BehinderungenListe, type BehinderungenParams } from '../api/site-docs.api'

const LAYOUT_KEY = 'columnbar_layout_site_diary_hindrances'
const SORTIERBAR = new Set(['projectLabel', 'art', 'text', 'firstDay', 'lastDay', 'workDays', 'ongoing', 'costExpected', 'notifiedOn'])

type SpaltenSchluessel = (typeof TAGEBUCH_BEHINDERUNG_SPALTEN)[number]['key']

interface ColDef {
  key: SpaltenSchluessel
  label: string
  sortable: boolean
}

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

const DEFAULT_COLUMN_KEYS = TAGEBUCH_BEHINDERUNG_SPALTEN.filter((s) => s.key !== 'effect').map((s) => s.key as string)
const DEFAULT_FILTER_FIELD_KEYS = ['project', 'from', 'to', 'art', 'ongoing', 'costExpected']
const STANDARD: ColumnState = { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortKey: 'firstDay', sortDir: 'desc' }

function filterParams(filters: Record<string, ActiveFilter>): BehinderungenParams {
  const p: BehinderungenParams = {}
  for (const [key, a] of Object.entries(filters)) {
    if (!a.value) continue
    if (key === 'q') p.q = a.value
    if (key === 'project') {
      const [quelle, id] = a.value.split(':')
      if (quelle === 'PROJECT') p.projectId = id
      if (quelle === 'TIME_REF') p.projectRefId = id
    }
    if (key === 'from') p.from = a.value.slice(0, 10)
    if (key === 'to') p.to = a.value.slice(0, 10)
    if (key === 'art') p.art = a.value as TagebuchBehinderungArt
    if (key === 'ongoing') p.ongoing = a.value === 'true'
    if (key === 'costExpected') p.costExpected = a.value === 'true'
  }
  return p
}

export default function BehinderungenPage() {
  const { t } = useTranslation()
  const terms = useTerminology()
  const { hasModule } = useAuthStore()
  const [projekte, setProjekte] = useState<SiteDocProjectChoice[]>([])

  useEffect(() => {
    fetchProjectChoices()
      .then((r) => setProjekte(r.data))
      .catch(() => setProjekte([]))
  }, [])

  const filterFields: FilterFieldDef[] = useMemo(
    () => [
      { key: 'q', label: t('siteDocs.diary.hindranceFilter.q', { project: terms.project }), type: 'text', category: 'core', operators: ['contains'] },
      { key: 'project', label: terms.project, type: 'select', category: 'core', operators: ['is'], options: projekte.map((p) => ({ value: `${p.source}:${p.id}`, label: [p.number, p.name].filter(Boolean).join(' ') })) },
      { key: 'from', label: t('siteDocs.filter.from'), type: 'date', category: 'core', operators: ['is'] },
      { key: 'to', label: t('siteDocs.filter.to'), type: 'date', category: 'core', operators: ['is'] },
      { key: 'art', label: t('siteDocs.diary.hindranceKind'), type: 'select', category: 'core', operators: ['is'], options: TAGEBUCH_BEHINDERUNG_ARTEN.map((a) => ({ value: a, label: t(`siteDocs.diary.hindranceKinds.${a}`) })) },
      { key: 'ongoing', label: t('siteDocs.diary.ongoing'), type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'costExpected', label: t('siteDocs.diary.extraCosts'), type: 'boolean', category: 'core', operators: ['is'] },
    ],
    [t, terms.project, projekte],
  )
  const alleSpalten: ColDef[] = useMemo(
    () => TAGEBUCH_BEHINDERUNG_SPALTEN.map((s) => ({ key: s.key, label: s.key === 'projectLabel' ? terms.project : t(`siteDocs.diary.hindranceColumns.${s.key}`), sortable: SORTIERBAR.has(s.key) })),
    [t, terms.project],
  )

  const ladeSpalten = (): ColumnState => {
    const g = loadUiSetting<Partial<ColumnState> | null>(LAYOUT_KEY, null)
    return g?.columnKeys?.length ? { columnKeys: g.columnKeys, columnWidths: g.columnWidths ?? {}, sortKey: g.sortKey ?? 'firstDay', sortDir: g.sortDir ?? 'desc' } : STANDARD
  }

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(ladeSpalten)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [result, setResult] = useState<BehinderungenListe | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, seite: number, state: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        setResult(await fetchBehinderungen({ ...filterParams(filters), page: seite, limit: 50, sortBy: state.sortKey, sortDir: state.sortDir }))
      } catch (e) {
        setError(fehlertext(e, t))
      } finally {
        setLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    void fetchData(filterValues, page, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(neu, 1, colState), 400)
    },
    [fetchData, colState],
  )

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleSort = (key: string) => {
    const dir: 'asc' | 'desc' = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: dir }
    updateColState(() => next)
    setPage(1)
    void fetchData(filterValues, 1, next)
  }

  const handleDrop = (ziel: string) => {
    const quelle = dragKeyRef.current
    setDragOverKey(null)
    if (!quelle || quelle === ziel) return
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const von = keys.indexOf(quelle)
      const nach = keys.indexOf(ziel)
      if (von < 0 || nach < 0) return prev
      keys.splice(von, 1)
      keys.splice(nach, 0, quelle)
      return { ...prev, columnKeys: keys }
    })
  }

  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    const startX = e.clientX
    const onMove = (ev: MouseEvent) => updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [key]: Math.max(60, startW + ev.clientX - startX) } }))
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  if (!hasModule('SITE_DOCS.DIARY')) return <ModuleLockedPage moduleKey="SITE_DOCS.DIARY" />

  const shownColumns = colState.columnKeys.map((k) => alleSpalten.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const availableToAdd = alleSpalten.filter((c) => !colState.columnKeys.includes(c.key))
  const ja = t('siteDocs.evidence.yes')

  const renderCell = (z: TagebuchBehinderungsKette, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'projectLabel':
        return z.projectLabel || '—'
      case 'art':
        return t(`siteDocs.diary.hindranceKinds.${z.art}`)
      case 'text':
        return <span className="block max-w-md truncate" title={z.text}>{z.text}</span>
      case 'firstDay':
        return fmtDate(z.firstDay)
      case 'lastDay':
        return fmtDate(z.lastDay)
      case 'workDays':
        return <span className="tabular-nums">{z.workDays}</span>
      case 'ongoing':
        return z.ongoing ? <span className="font-medium text-amber-700 dark:text-amber-300">{ja}</span> : ''
      case 'costExpected':
        return z.costExpected ? ja : ''
      case 'notifiedOn':
        return z.notifiedOn ? fmtDate(z.notifiedOn) : ''
      case 'effect':
        return z.effect ?? ''
      case 'reportsText':
        return z.reports.join(', ')
      default:
        return null
    }
  }

  const rows = result?.data ?? []
  const meta = result?.meta
  const totalCols = shownColumns.length + (columnConfigMode && availableToAdd.length ? 1 : 0) + 1
  const pfeil = (key: string) =>
    colState.sortKey === key ? colState.sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />
  const titel = t('mailExport.titles.EXPORT_SITE_DIARY_HINDRANCES')

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <Link to="/site-docs/diary" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {t('siteDocs.diary.bookTitle')}
          </Link>
          <h1 className="text-3xl font-bold text-foreground">{t('siteDocs.diary.hindranceList')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('siteDocs.diary.hindranceListSubtitle')}</p>
        </div>
        <ExportButton
          defaultFilename="behinderungen"
          options={[
            { format: 'xlsx', labelKey: 'export.xlsx' },
            { format: 'pdf', labelKey: 'export.pdf' },
          ]}
          request={(format, orientation) =>
            behinderungenExportRequest({ ...filterParams(filterValues), sortBy: colState.sortKey, sortDir: colState.sortDir, format, orientation }, shownColumns.map((c) => ({ key: c.key, label: c.label })))
          }
          mail={{ source: 'EXPORT_SITE_DIARY_HINDRANCES', title: titel, subject: titel }}
        />
      </div>

      <FilterBar availableFields={filterFields} value={filterValues} onChange={handleFiltersChange} layoutKey="site_diary_hindrances" defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS} />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          title={t('siteDocs.list.columns')}
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? STANDARD
                markUiSettingApplied(LAYOUT_KEY, next)
                setColState(next)
                void fetchData(filterValues, 1, next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 text-left font-medium text-foreground ${columnConfigMode ? 'cursor-grab' : ''} ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => { e.preventDefault(); setDragOverKey(col.key) } : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? () => setDragOverKey(null) : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {col.sortable ? (
                        <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                          {col.label} {pfeil(col.key)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button type="button" onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))} className="ml-1 text-muted-foreground hover:text-destructive" title={t('siteDocs.list.removeColumn')}>
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">{t('siteDocs.list.addColumn')}</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('siteDocs.diary.hindranceEmpty')}
                  </td>
                </tr>
              )}
              {rows.map((z, idx) => (
                <tr key={z.chainId} className={`border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}>
                  {shownColumns.map((col) => (
                    <td key={col.key} className="whitespace-nowrap px-4 py-1.5">
                      {renderCell(z, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  {shownColumns.map((col, i) => (
                    <td key={col.key} className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                      {i === 0 ? t('siteDocs.diary.hindranceCount', { count: meta?.total ?? rows.length }) : null}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>{t('siteDocs.list.page', { page: meta.page, pages: meta.totalPages, total: meta.total })}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ‹
              </button>
              <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
